/**
 * Marketing Tab — texts, photos, video for the Hub
 */
import { useState, useCallback, useRef } from 'react';
import { ImagePlus, Check, Loader2, Type, Eye } from 'lucide-react';
import { updateAdminCatalogSystem, uploadAdminCatalogSystemImage, type AdminCatalogSystem } from '@/api';
import { bindAdminImageCrop } from '@/hooks/useAdminImageCrop';

interface MarketingTabProps {
  system: AdminCatalogSystem;
  onSave?: (system: AdminCatalogSystem) => void;
}

export function MarketingTab({ system, onSave }: MarketingTabProps) {
  const [title, setTitle] = useState(system.title || '');
  const [shortDesc, setShortDesc] = useState(system.short_description || '');
  const [fullDesc, setFullDesc] = useState(
    (() => {
      const fd = system.full_description;
      if (!fd) return '';
      if (typeof fd === 'string') {
        try { const p = JSON.parse(fd); return p?.admin_full_description || p?.text || JSON.stringify(p, null, 2); } catch { return fd; }
      }
      if (typeof fd === 'object') {
        const o = fd as Record<string, unknown>;
        return String(o.admin_full_description || o.text || JSON.stringify(fd, null, 2));
      }
      return '';
    })()
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const { cropModal, openFromFile } = bindAdminImageCrop();

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const updated = await updateAdminCatalogSystem(system.id, {
        title,
        short_description: shortDesc,
        full_description: fullDesc ? JSON.stringify({ admin_full_description: fullDesc }) : undefined,
      });
      onSave?.(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setIsSaving(false);
    }
  }, [system.id, title, shortDesc, fullDesc, onSave]);

  const uploadImageFile = async (file: File) => {
    setIsUploading(true);
    try {
      const updated = await uploadAdminCatalogSystemImage(system.id, file);
      onSave?.(updated);
    } finally {
      setIsUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await openFromFile({
      file,
      defaultPreset: 'catalog_wide',
      allowedPresets: ['catalog_wide', 'square', 'full_size', 'vitrine_tile'],
      title: 'Главное фото',
      onConfirm: uploadImageFile,
      onConfirmRaw: uploadImageFile,
    });
  };

  return (
    <div className="space-y-5">
      {cropModal}

      {/* How-it-works hint */}
      <div className="px-3.5 py-3 rounded-xl text-[11px] leading-relaxed space-y-1" style={{ background: 'rgba(104,211,145,0.07)', border: '1px solid rgba(104,211,145,0.2)' }}>
        <p className="font-bold" style={{ color: '#68D391' }}>💡 Как это влияет на конфигуратор</p>
        <p className="text-white/60"><span className="text-white/80 font-semibold">Название</span> — показывается как заголовок карточки и в табах выбора системы</p>
        <p className="text-white/60"><span className="text-white/80 font-semibold">Краткое описание</span> — показывается под названием в карточке (1-2 предложения)</p>
        <p className="text-white/60"><span className="text-white/80 font-semibold">Полное описание</span> — раздел «Кратко» внизу карточки конфигуратора</p>
        <p className="text-white/60"><span className="text-white/80 font-semibold">Фото</span> — главное фото карточки и галерея</p>
      </div>

      {/* Title */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
          <Type size={11} /> Название системы
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#68D391]/50 focus:outline-none transition-colors text-white placeholder-white/30"
          placeholder="Например: Onviz MR-2234 Белый профиль"
        />
      </div>

      {/* Short Description */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
          <Eye size={11} /> Краткое описание
          <span className="ml-1 px-1.5 py-0.5 rounded text-[9px]" style={{ background: 'rgba(252,129,74,0.15)', color: '#FC814A' }}>показывается под названием</span>
        </label>
        <textarea
          value={shortDesc}
          onChange={(e) => setShortDesc(e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#68D391]/50 focus:outline-none transition-colors resize-none text-white placeholder-white/30"
          placeholder="Бесшумный электрокарниз с Wi-Fi управлением. Плавный старт и стабильный ход."
        />
        {shortDesc && (
          <div className="px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(255,255,255,0.03)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}>
            <span className="text-white/30 text-[10px] mr-1">Превью:</span>{shortDesc}
          </div>
        )}
      </div>

      {/* Full Description */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
          <Eye size={11} /> Полное описание
          <span className="ml-1 px-1.5 py-0.5 rounded text-[9px]" style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED' }}>раздел «Кратко» внизу</span>
        </label>
        <textarea
          value={fullDesc}
          onChange={(e) => setFullDesc(e.target.value)}
          rows={5}
          className="w-full px-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] focus:border-[#63B3ED]/50 focus:outline-none transition-colors resize-none text-white placeholder-white/30"
          placeholder="Подробное описание системы. Особенности, преимущества, технологии..."
        />
      </div>

      {/* Photo */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>
          <ImagePlus size={11} /> Главное фото
        </label>
        <div className="flex items-center gap-3">
          {system.image_url ? (
            <img src={system.image_url} alt={system.title} className="w-20 h-20 rounded-xl object-cover border border-white/10" />
          ) : (
            <div className="w-20 h-20 rounded-xl flex items-center justify-center bg-white/[0.04] border border-dashed border-white/20">
              <span className="text-[10px] text-white/30">Нет фото</span>
            </div>
          )}
          <label className="flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer transition-colors hover:bg-white/[0.06] border border-dashed border-white/30">
            <ImagePlus size={14} className="text-white/50" />
            <span className="text-sm text-white/70">{isUploading ? 'Загрузка...' : 'Загрузить'}</span>
            <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => void handleImagePick(e)} disabled={isUploading} />
          </label>
          {isUploading && <Loader2 size={14} className="animate-spin text-white/50" />}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
          style={saved
            ? { background: 'rgba(104,211,145,0.25)', color: '#68D391', border: '1px solid rgba(104,211,145,0.4)' }
            : { background: 'rgba(104,211,145,0.15)', color: '#68D391', border: '1px solid rgba(104,211,145,0.25)' }}
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {isSaving ? 'Сохранение...' : saved ? 'Сохранено ✓' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
